import { DateTime } from "luxon";

import { ISalvage, ISetSalvageRequest } from "../data/useSalvage";

/** Selling price minus collection cost, selling fees, VAT and what SAV paid. */
export function salvageProfit(
  actualAuctionValue: number | null | undefined,
  collectionCost: number | null | undefined,
  finalSellingFees: number | null | undefined,
  vatCharges: number | null | undefined,
  vehiclePrice: number | null | undefined
): number | null {
  if (actualAuctionValue == null || Number.isNaN(actualAuctionValue)) return null;
  return (
    actualAuctionValue -
    (collectionCost || 0) -
    (finalSellingFees || 0) -
    (vatCharges || 0) -
    (vehiclePrice || 0)
  );
}

/** Whole days from Copart collection to the sold day, the return day, or today. */
export function salvageDaysInStock(
  copartCollectedOn?: string | null,
  soldOn?: string | null,
  today: DateTime = DateTime.utc(),
  returnedOn?: string | null
): number | null {
  if (!copartCollectedOn) return null;
  const start = DateTime.fromISO(copartCollectedOn, { zone: "utc" }).startOf("day");
  if (!start.isValid) return null;
  const endSource = soldOn || returnedOn;
  const end = endSource
    ? DateTime.fromISO(endSource, { zone: "utc" }).startOf("day")
    : today.toUTC().startOf("day");
  if (!end.isValid) return null;
  const days = Math.floor(end.diff(start, "days").days);
  return days < 0 ? 0 : days;
}

/** yyyy-MM-dd for the API date fields, or null to clear. */
export function salvageDay(value?: string | null): string | null {
  if (!value) return null;
  const day = value.trim().slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null;
}

export function salvageMoney(value: string | number | null | undefined): number | null {
  if (value === "" || value == null) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Full save body from a list row, with one field overwritten. */
export function salvageRequestFromRow(row: ISalvage, patch: Partial<ISetSalvageRequest> = {}): ISetSalvageRequest {
  return {
    copartInstructed: !!row.copartInstructed,
    copartCollectedOn: salvageDay(row.copartCollectedOn),
    collectionCost: salvageMoney(row.collectionCost),
    capValue: salvageMoney(row.capValue),
    proquoteValue: salvageMoney(row.proquoteValue),
    projectedAuctionValue: salvageMoney(row.projectedAuctionValue),
    auctionRun1: salvageMoney(row.auctionRun1),
    auctionRun2: salvageMoney(row.auctionRun2),
    auctionRun3: salvageMoney(row.auctionRun3),
    auctionRun4: salvageMoney(row.auctionRun4),
    actualAuctionValue: salvageMoney(row.actualAuctionValue),
    soldOn: salvageDay(row.soldOn),
    finalSellingFees: salvageMoney(row.finalSellingFees),
    vatCharges: salvageMoney(row.vatCharges),
    returnedToGp: !!row.returnedToGp,
    redeliveryCost: salvageMoney(row.redeliveryCost),
    ...patch,
  };
}
